import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router";
import { useJobType, useUpdateJobType, useCloneJobType } from "../hooks/useJobTypes";
import { useJobTypeRegistryFields } from "../hooks/useJobTypeRegistryFields";
import { useCustomCounters } from "../counters/hooks/useCustomCounters";
import { useCurrentTenant } from "@/hooks/useCurrentTenant";
import { useEntitlements } from "@/hooks/useEntitlements";
import { CloneJobTypeDialog } from "../components/CloneJobTypeDialog";
import { GeneralTab } from "../components/GeneralTab";
import JobTypeDisplayTemplatesSection from "../jobtype/components/JobTypeDisplayTemplatesSection";
import { ConsolidatedPipelineTab } from "./pipeline/ConsolidatedPipelineTab";
import { CalibrationStatus } from "../components/CalibrationStatus";
import { FloorConfigEditor } from "../components/FloorConfig";
import { HandwritingSettings, DEFAULT_HANDWRITING_CONFIG } from "../components/HandwritingSettings";
import type { HandwritingConfig } from "../components/HandwritingSettings";
import { JobTypeLlmTab } from "./JobTypeLlmTab";
import { IntelligenceTab } from "../components/IntelligenceTab";
import { JobTypeSurfaceLink } from "../components/JobTypeSurfaceLink";
import { SetupCompass } from "../components/SetupCompass";
import { useJobTypeForms } from "../form-config/forms/useJobTypeForms";
import { HeaderTab } from "../form-config/header/HeaderTab";
import { DestinationListTab } from "../form-config/export/DestinationListTab";
import { AutomationsTab } from "../automations/AutomationsTab";
import { visibleTabsFor, type JobTab } from "../automations/visible-tabs";
import { RemittanceTab } from "../form-config/remittance/RemittanceTab";
import { type Surface } from "@capture/shared";
import "../admin.css";

const TABS = [
  { id: "general", label: "General" },
  { id: "header", label: "Header" },
  { id: "pipeline", label: "Pipeline" },
  { id: "calibration", label: "Calibration" },
  { id: "handwriting", label: "Handwriting" },
  { id: "llm", label: "LLM" },
  { id: "intelligence", label: "Intelligence" },
  { id: "export-destinations", label: "Export Destinations" },
] as const;

type TabId = (typeof TABS)[number]["id"] | "automations" | "remittance";

/**
 * Followups #7 — old tabs that moved to FormConfigPage. Stale ?tab=
 * values on /admin/job-types/:id are transparently redirected to the
 * FormConfig equivalent (form -> fields, others identity). Replaces
 * the previous in-page stub tabs that displayed "Open Form Configuration"
 * buttons.
 */
const STALE_TABS: Record<string, string> = {
  form: "fields",
  export: "export-mapping",
  zones: "zones",
  "ocr-profiles": "ocr-profiles",
  // P3c (2026-07-08) — `rules` no longer bounces to the retired Form Config
  // Rules tab. It is now aliased to the local Automations tab (see the tab
  // resolution below), so it is intentionally NOT in this redirect map.
};

export function JobTypeDetailPage() {
  const { jobTypeId: id } = useParams<{ jobTypeId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { jobType, isLoading, error, applyLocal } = useJobType(id);
  // SP1 §5.1 — the tab strip is surface-scoped. P3c (2026-07-08) — the
  // Automations tab is unconditional (opt-out retired); visibleTabsFor appends
  // it, then filters by surface (identical to standard today, no remittance tabs).
  const surface: Surface = (jobType?.surface ?? "standard") as Surface;
  const visibleTabs = visibleTabsFor(TABS as readonly JobTab[], surface);
  // Followups #7 — guard against stale (or unknown) tab values in the URL.
  // Stale values (form/rules/export) are redirected to FormConfig
  // by the useEffect added in Phase 3; until that effect fires, fall back
  // to "general" so the tab strip stays valid.
  // 2026-06-28 — the stages/routing skeleton used to live under ?tab=workflow.
  // That tab was consolidated into the new "Pipeline" tab (Stages sub-tab) when
  // Pipeline moved to Job Config. Alias old ?tab=workflow bookmarks and the
  // lingering QC-sampling deep links so they land on the Pipeline tab instead
  // of silently falling back to General.
  const rawTab = searchParams.get("tab");
  // 2026-06-28 — old ?tab=workflow bookmarks land on the consolidated Pipeline
  // tab. P3c (2026-07-08) — the retired Form Config Rules tab is replaced by
  // the local Automations tab, so alias legacy ?tab=rules bookmarks to it.
  const tabFromUrl =
    rawTab === "workflow" ? "pipeline" : rawTab === "rules" ? "automations" : rawTab;
  const activeTab: TabId =
    tabFromUrl && visibleTabs.some((t) => t.id === tabFromUrl)
      ? (tabFromUrl as TabId)
      : "general";

  const { entitlements } = useEntitlements();
  const { fields: registryFields, rawFields, patchFields } = useJobTypeRegistryFields(id ?? "");
  const { update, isSubmitting } = useUpdateJobType(id ?? "");
  // R-167 — counters feed the JobTypeDisplayTemplatesSection's
  // knownCounterNames context (drives the {counter:NAME} validator chip).
  // Tenant fallback feeds the inherit-from-tenant input placeholder.
  // Defensive ?? [] guards against test fixtures that mock api.get
  // without a `counters` field in the response (e.g. shared api mocks).
  const { counters: rawCounters } = useCustomCounters();
  const counters = rawCounters ?? [];
  const { data: tenant } = useCurrentTenant();
  // Phase 1.5 WU-2 — CalibrationStatus needs the JobTypeForm id as its
  // `profileId` (mirrors `syncCaptureProfileForJobType` on the API side, where
  // `OcrProfile.profileId = defaultForm.id`). Falls back to empty list while
  // loading; the panel handles the empty state gracefully.
  const { forms } = useJobTypeForms(id ?? "");
  const defaultFormId = forms.find((f) => f.isDefault)?.id ?? forms[0]?.id ?? "";
  const { clone } = useCloneJobType();
  const [showClone, setShowClone] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  // F-001 residue (2026-07-08 walk) — General uses an explicit Save bar
  // (it PATCHes the live JobType, not a draft blob) while sibling tabs
  // autosave, so a switch away from General with unsaved edits used to be
  // silent. Track General's dirty state here so tab switches and full page
  // navigation can warn before discarding it.
  const [generalDirty, setGeneralDirty] = useState(false);

  // Followups #7 — redirect stale ?tab= bookmarks to FormConfig once the
  // job type resolves. `replace: true` keeps the back button clean.
  useEffect(() => {
    if (!jobType) return;
    const stale = searchParams.get("tab");
    if (stale && stale in STALE_TABS) {
      navigate(`/admin/forms/${jobType.id}?tab=${STALE_TABS[stale]}`, {
        replace: true,
      });
    }
  }, [jobType, searchParams, navigate]);

  // F-001 residue — warn on full page navigation / tab close while General
  // has unsaved edits, mirroring the ZonesTab beforeunload pattern.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (generalDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [generalDirty]);

  if (!id) {
    return <div className="admin-error">No job type selected</div>;
  }

  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="admin-loading"><div className="loading-spinner" /></div>
      </div>
    );
  }

  if (error || !jobType) {
    return (
      <div className="admin-page">
        <div className="admin-error">Error: {error ?? "Not found"}</div>
      </div>
    );
  }

  const setTab = (tab: TabId) => {
    // F-001 residue — General doesn't autosave, so switching away with
    // unsaved edits used to lose them silently. Confirm before discarding.
    if (generalDirty && activeTab === "general" && tab !== "general") {
      const proceed = window.confirm(
        "You have unsaved changes on General. Discard them and switch tabs?",
      );
      if (!proceed) return;
    }
    setSearchParams({ tab });
  };

  const handleSave = async (data: Record<string, unknown>) => {
    // R-126 (2026-05-23) — patch local state with the PATCH response
    // instead of re-fetching via refresh({ bypassCache: true }). The old
    // refresh path called setIsLoading(true) → the Calibration tab's floor
    // editors unmounted/remounted, lost focus, and discarded the in-flight
    // edit, which compounded with FloorConfigEditor's autosave-on-keystroke
    // (R-126 fix below) to create a churning save+reload loop per spinner
    // step. The PATCH endpoint already returns the updated row, so the
    // follow-up GET was redundant; the cache-bypass query param is
    // unnecessary on the save path now that we don't re-read at all.
    //
    // (R-60a — the original reason for bypassCache — is satisfied because
    // we never look at the stale server cache; we trust the PATCH response.)
    const next = await update(data);
    applyLocal(next);
  };

  const handleClone = async (name: string) => {
    if (!jobType) return;
    setIsCloning(true);
    try {
      const newJt = await clone(jobType.id, name);
      setShowClone(false);
      navigate(`/admin/job-types/${newJt.id}`);
    } finally {
      setIsCloning(false);
    }
  };

  // WU-10 / R-130 — Publish cross-link. The Publish flow lives only on the
  // Form Config surface (`/admin/forms/:jobTypeId`), but operators land here
  // from the Job Types list to configure OCR Profiles / Zones / Calibration
  // and then look for Publish. Surface a small pill that links them across
  // when there are unpublished changes. Pure read of the API-computed flag —
  // the pill never publishes anything itself (see DraftBanner for that).
  // Guarded for older API responses that may omit the field (treat as false).
  const hasUnpublishedChanges =
    (jobType as { hasUnpublishedChanges?: boolean }).hasUnpublishedChanges ===
    true;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="admin-back" onClick={() => navigate("/admin/job-types")}>
          &larr; Job Types
        </button>
        {/* WU-10 / R-130 — Renamed from "Processing Config" to "Job Config"
            so the noun stays consistent with the entry-point ("Job Types"
            list) and operators don't experience a name break when they
            click into a row. Route, entity, and internal identifiers (e.g.
            COMPASS_STEPS `page: "processing"`) are unchanged — pure label
            change. */}
        <h1 className="admin-title">Job Config · {jobType.name}</h1>
        {!jobType.isActive && (
          <span className="jt-badge jt-badge--inactive">Inactive</span>
        )}
        {hasUnpublishedChanges && (
          <Link
            to={`/admin/forms/${jobType.id}`}
            className="jt-publish-pill"
            data-testid="jt-publish-pill"
          >
            Form Config has unpublished changes &rarr;
          </Link>
        )}
        <div className="admin-header-actions">
          <button
            className="jt-action-btn"
            onClick={() => setShowClone(true)}
          >
            Clone
          </button>
          <JobTypeSurfaceLink jobTypeId={jobType.id} currentSurface="processing" />
        </div>
      </div>

      {jobType.surface === "remittance" && entitlements !== null && !entitlements.remittance && (
        <div className="jt-dormant-notice jt-dormant-notice--page" role="status">
          Remittance subscription inactive — new batch intake is paused; in-flight work can
          complete.
        </div>
      )}

      <SetupCompass
        jobTypeId={jobType.id}
        surface={surface}
        activeStepId={
          ["general", "pipeline", "calibration", "automations"].includes(activeTab as string)
            ? activeTab
            : ""
        }
      />

      <div className="tab-bar">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? "tab-btn--active" : ""}`}
            onClick={() => setTab(tab.id as TabId)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === "general" && (
          <>
            <GeneralTab
              jobType={jobType}
              onSave={handleSave}
              isSaving={isSubmitting}
              onDirtyChange={setGeneralDirty}
            />
            {/* R-167 — per-job-type display templates. The inherit toggle
                consults the tenant-level fallback; the validator chips need
                to know which counters + fields exist for this job type. */}
            <JobTypeDisplayTemplatesSection
              jobType={{
                id: jobType.id,
                name: jobType.name,
                shortName: jobType.shortName ?? "",
                batchDisplayTemplate: jobType.batchDisplayTemplate ?? null,
                documentDisplayTemplate: jobType.documentDisplayTemplate ?? null,
                pageDisplayTemplate: jobType.pageDisplayTemplate ?? null,
                // R-167 WU-8 — opt-in flag for which identifier drives this
                // job type's export "doc number" column.
                defaultDocNumberSource: jobType.defaultDocNumberSource ?? null,
              }}
              tenantFallback={{
                batchDisplayTemplate: tenant?.batchDisplayTemplate ?? null,
                documentDisplayTemplate: tenant?.documentDisplayTemplate ?? null,
                pageDisplayTemplate: tenant?.pageDisplayTemplate ?? null,
              }}
              knownCounterNames={
                new Set(
                  counters
                    .filter(
                      (c) => c.jobTypeId === jobType.id || c.jobTypeId === null,
                    )
                    .map((c) => c.name),
                )
              }
              knownFieldInternalNames={
                new Set(
                  rawFields.map(
                    (f) => f.internalName ?? f.name,
                  ),
                )
              }
              onSave={(patch) => handleSave(patch as Record<string, unknown>)}
            />
          </>
        )}
        {activeTab === "header" && <HeaderTab />}
        {activeTab === "pipeline" && (
          <ConsolidatedPipelineTab jobTypeId={jobType.id} fields={registryFields} surface={surface} />
        )}
        {activeTab === "calibration" && (
          <div>
            {/* E-058 — value-story header: the tab read as valueless because
                nothing on screen said what the flywheel IS or what to do
                first. This also names the tie to the verification station's
                attention model (the floors and the F-046 counter are the same
                mechanism). */}
            <p className="rb-hint" data-testid="calibration-value-story">
              Confidence floors decide which fields operators must touch at the
              verification station — fields at or above their floor are
              auto-verified and skipped; fields below it stop for review (they
              are the station&apos;s &ldquo;fields need attention&rdquo;
              count). Floors apply to fields whose review disposition is the
              default &ldquo;Stop if below its confidence floor&hellip;&rdquo;
              — a field set to &ldquo;Always stop for review&rdquo; or
              &ldquo;Never stop&rdquo; ignores its floor. Enable audit sampling
              below to measure whether your floors are right: sampled fields
              are re-checked by operators, and recommended floors appear in
              this table as samples accumulate.
            </p>
            {/*
              Phase 1.5 WU-2 (R-126 backend) — CalibrationStatus is now keyed
              by (profileId, fieldId). `profileId` is the default JobTypeForm
              id (mirrors syncCaptureProfileForJobType server-side); each
              field is a FieldDefinition row from the form's registry.
              Empty/missing form → empty-state panel.
            */}
            <CalibrationStatus
              tenantId={jobType.tenantId}
              profileId={defaultFormId}
              fields={rawFields.map((f) => ({
                id: f.fieldDefinitionId,
                name: f.label ?? f.internalName ?? f.fieldDefinitionId,
              }))}
            />
            <FloorConfigEditor
              thresholds={(jobType.confidenceThresholds as any) || {}}
              onChange={(t) => handleSave({ confidenceThresholds: t })}
            />
            {/*
              Phase 1.5 WU-4 (R-127, 2026-05-23) — `FocusModeThresholdEditor`
              REMOVED. Smart-skip is now the default keying mode; the
              per-type floor at 1.00 covers the "key everything" case that
              the doc-level Focus Attention Threshold used to. The
              operator-level `autoFocusMode` / `defaultFocusMode` toggles
              on the Operator Preferences page are unaffected (different
              concept — per-operator UI toggle vs per-job-type threshold).
            */}
            <div className="gt-section">
              <h3 className="rb-section-title">Audit Sampling</h3>
              {/*
                R-60d (root cause, 2026-05-17) — the checkbox/slider are fully
                controlled by server state (checked={jobType.auditCalibration
                Enabled}). The PATCH /api/job-types/:id body is validated by
                updateJobTypeSchema with additionalProperties:false; until
                2026-05-17 `auditCalibrationEnabled`/`auditSampleRate` were NOT
                whitelisted, so Fastify+ajv `removeAdditional` silently stripped
                them — the round-trip never changed the value and the control
                appeared permanently frozen. Fixed by adding both fields to the
                schema whitelist (apps/api/src/routes/job-types/schemas.ts).
                (An earlier 2026-05-14 change here swapped .gt-checkbox-row →
                .gt-toggle assuming a CSS hit-target cause; that was a
                misdiagnosis — kept because .gt-toggle is still the correct
                canonical row style, but it was never the bug.)
              */}
              <label className="gt-toggle">
                <input
                  type="checkbox"
                  checked={jobType.auditCalibrationEnabled ?? false}
                  onChange={(e) =>
                    handleSave({ auditCalibrationEnabled: e.target.checked })
                  }
                />
                {/* E-058↔E-022 glossary pairing — canonical term is "audit
                    sampling" (matches the section title and the
                    CalibrationStatus zero-samples note). */}
                <span>Enable audit sampling</span>
              </label>
              {/* E-058 — consequence line: what enabling actually does, what it
                  costs operators, and where the results land. */}
              <p className="rb-hint">
                Routes the chosen percentage of this job&apos;s documents to a
                full-review audit pass (operators re-key every field on those
                documents). The collected ground truth feeds the samples and
                recommended floors in the table above.
              </p>
              {jobType.auditCalibrationEnabled && (
                <>
                  <div className="gt-slider-row">
                    <span className="gt-label">Sample Rate</span>
                    <input
                      type="range"
                      min="0.01"
                      max="1.0"
                      step="0.01"
                      value={jobType.auditSampleRate ?? 0.1}
                      onChange={(e) =>
                        handleSave({
                          auditSampleRate: parseFloat(e.target.value),
                        })
                      }
                    />
                    <span className="gt-slider-value">
                      {((jobType.auditSampleRate ?? 0.1) * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p className="rb-hint">
                    Percentage of documents routed to full-review audit for
                    calibration training data.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
        {activeTab === "handwriting" && (
          <HandwritingSettings
            jobTypeId={jobType.id}
            config={(jobType.handwritingConfig as unknown as HandwritingConfig) ?? DEFAULT_HANDWRITING_CONFIG}
            onSave={(cfg) => handleSave({ handwritingConfig: cfg })}
          />
        )}
        {activeTab === "llm" && (
          <JobTypeLlmTab
            fieldDefinitions={rawFields.map((f) => ({
              id: f.fieldDefinitionId,
              name: f.internalName ?? f.name,
              label: f.label,
              dataType: f.dataType,
              llmRoutingMode: f.llmRoutingMode,
            }))}
            llmConfig={(jobType as any).llmConfig}
            onSave={handleSave}
            onFieldRoutingChange={(fieldId, mode) =>
              patchFields([{ fieldDefinitionId: fieldId, changes: { llmRoutingMode: mode } }])
            }
          />
        )}
        {activeTab === "export-destinations" && (
          // Plan 3 Task 5 — job-level export destinations (multi-destination,
          // no per-form field mapping). Reads/writes V3 `content.exportDestinations`.
          // Routing needs RoutingFieldSummary[]; `rawFields` are draft FieldContent
          // rows, mapped here the same way FormConfigPage feeds ExportComposer.
          <DestinationListTab
            jobTypeId={jobType.id}
            fields={rawFields.map((f) => ({
              name: f.name,
              label: f.label || f.name,
              dataType: f.dataType,
              stageMap: f.stageMap,
            }))}
            processingMode={jobType.processingMode ?? undefined}
            jobTypeDefaultPolicy={jobType.defaultExportTriggerPolicy ?? undefined}
            surface={surface}
          />
        )}
        {activeTab === "automations" && <AutomationsTab surface={surface} />}
        {activeTab === "remittance" && <RemittanceTab jobTypeId={jobType.id} />}
        {activeTab === "intelligence" && (
          <IntelligenceTab
            jobTypeId={id}
            verifyAutoCorrections={(jobType as any).verifyAutoCorrections ?? true}
            onToggleVerify={(value) => handleSave({ verifyAutoCorrections: value })}
            reverifyValve={jobType.reverifyValve ?? undefined}
            onSaveValve={(next) => handleSave({ reverifyValve: next })}
            isValveSaving={isSubmitting}
          />
        )}
      </div>

      {showClone && (
        <CloneJobTypeDialog
          sourceName={jobType.name}
          isSubmitting={isCloning}
          onSubmit={handleClone}
          onClose={() => setShowClone(false)}
        />
      )}
    </div>
  );
}
